import React, { useMemo, useState } from 'react';
import Grid from '@mui/material/GridLegacy';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { Delete, Search, FilterList, TableChart, ExpandMore, ExpandLess } from '@mui/icons-material';
import { Collapse, IconButton } from '@mui/material';
import {
  adminTabCardContentSx,
  adminTabCardSx,
  adminTabPageSubtitleSx,
  adminTabPageTitleSx,
  adminGradientContainedSx,
  adminDialogTitleSx,
} from '../styles/adminTabStyles';

function isValidMongoObjectId(s) {
  return typeof s === 'string' && /^[a-fA-F0-9]{24}$/.test(s.trim());
}

export default function RedemptionsTab({
  redemptions = [],
  total = 0,
  filters = {},
  onApplyFilters,
  onDeleteRedemption,
  deletionHistory = [],
  onLoadPlaceStats,
  placeStats,
}) {
  const [localFilters, setLocalFilters] = useState(filters);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [deleteReason, setDeleteReason] = useState('');
  const [expandedPlaces, setExpandedPlaces] = useState({});
  const [summarySortState, setSummarySortState] = useState({
    place: { key: 'total', direction: 'desc' },
    offer: { key: 'total', direction: 'desc' },
    ambassador: { key: 'total', direction: 'desc' },
  });

  const rows = useMemo(() => redemptions || [], [redemptions]);

  const handleSummarySort = (section, key) => {
    setSummarySortState((prev) => {
      const current = prev[section] || { key: 'total', direction: 'desc' };
      if (current.key === key) {
        return {
          ...prev,
          [section]: { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        };
      }
      return {
        ...prev,
        [section]: { key, direction: 'desc' }
      };
    });
  };

  const renderSummaryHeaderButton = (section, key, label) => {
    const sectionSort = summarySortState[section] || { key: 'total', direction: 'desc' };
    const active = sectionSort.key === key;
    return (
      <Button
        size="small"
        variant="text"
        onClick={() => handleSummarySort(section, key)}
        aria-label={label}
        sx={{
          minWidth: 0,
          px: 0.5,
          py: 0.25,
          textTransform: 'none',
          color: 'text.primary',
          fontWeight: 700,
          fontSize: 'inherit',
          ...(active ? { color: 'primary.main' } : {}),
        }}
      >
        {label}
        {active && <Box component="span" sx={{ ml: 0.5, fontSize: '0.8rem' }}>{sectionSort.direction === 'asc' ? '↑' : '↓'}</Box>}
      </Button>
    );
  };

  // Commission calculations
  const commissionStats = useMemo(() => {
    const stats = {
      totalOfferCommission: 0,
      totalAmbassadorCommission: 0,
      totalNeozoolaCommission: 0,
      totalMarketingFee: 0,
      byAmbassador: new Map(),
      byPlace: new Map(),
      byOffer: new Map(),
      placeOffers: new Map(), // For expandable offers under each place
    };

    rows.forEach((r) => {
      const offerComm = r.offerCommissionLe || 0;
      const ambassadorComm = r.ambassadorCommissionLe || 0;
      const neozoolaComm = offerComm - ambassadorComm;
      const marketingFee = r.marketingFeeLe || 0;

      stats.totalOfferCommission += offerComm;
      stats.totalAmbassadorCommission += ambassadorComm;
      stats.totalNeozoolaCommission += neozoolaComm;
      stats.totalMarketingFee += marketingFee;

      // By ambassador
      if (r.ambassadorCode) {
        const ambCode = r.ambassadorCode;
        if (!stats.byAmbassador.has(ambCode)) {
          stats.byAmbassador.set(ambCode, { code: ambCode, total: 0, neozoola: 0, ambassador: 0, count: 0 });
        }
        const ambStats = stats.byAmbassador.get(ambCode);
        ambStats.total += offerComm;
        ambStats.neozoola += neozoolaComm;
        ambStats.ambassador += ambassadorComm;
        ambStats.count += 1;
      }

      // By place
      const placeKey = `${r.placeId?._id || r.placeId}`;
      const placeName = r.placeId?.name || r.placeId || 'Unknown';
      if (!stats.byPlace.has(placeKey)) {
        stats.byPlace.set(placeKey, { placeId: placeKey, placeName, total: 0, neozoola: 0, ambassador: 0, marketingFee: 0, count: 0 });
        stats.placeOffers.set(placeKey, new Map());
      }
      const placeStats = stats.byPlace.get(placeKey);
      placeStats.total += offerComm;
      placeStats.neozoola += neozoolaComm;
      placeStats.ambassador += ambassadorComm;
      placeStats.marketingFee += marketingFee;
      placeStats.count += 1;

      // Offers under this place
      const offerKey = `${r.offerId}`;
      const offerName = r.offerName || r.offerId || 'Unknown';
      const placeOfferMap = stats.placeOffers.get(placeKey);
      if (!placeOfferMap.has(offerKey)) {
        placeOfferMap.set(offerKey, { offerId: offerKey, offerName, total: 0, neozoola: 0, ambassador: 0, count: 0 });
      }
      const placeOffer = placeOfferMap.get(offerKey);
      placeOffer.total += offerComm;
      placeOffer.neozoola += neozoolaComm;
      placeOffer.ambassador += ambassadorComm;
      placeOffer.count += 1;

      // By offer (overall)
      if (!stats.byOffer.has(offerKey)) {
        stats.byOffer.set(offerKey, { offerId: offerKey, offerName, places: new Set(), total: 0, neozoola: 0, ambassador: 0, count: 0 });
      }
      const offerStats = stats.byOffer.get(offerKey);
      offerStats.places.add(placeName);
      offerStats.total += offerComm;
      offerStats.neozoola += neozoolaComm;
      offerStats.ambassador += ambassadorComm;
      offerStats.count += 1;
    });

    return stats;
  }, [rows]);

  const sortedPlaceSummary = useMemo(() => {
    const items = Array.from(commissionStats.byPlace.values());
    const sectionSort = summarySortState.place || { key: 'total', direction: 'desc' };
    const getValue = (item) => {
      switch (sectionSort.key) {
        case 'count': return item.count;
        case 'total': return item.total;
        case 'marketingFee': return item.marketingFee || 0;
        case 'neozoola': return item.neozoola;
        case 'ambassador': return item.ambassador;
        default: return item.total;
      }
    };
    return [...items].sort((a, b) => {
      if (sectionSort.key === 'marketingFee') {
        const av = a.marketingFee || 0;
        const bv = b.marketingFee || 0;
        return sectionSort.direction === 'asc' ? av - bv : bv - av;
      }
      return sectionSort.direction === 'asc' ? getValue(a) - getValue(b) : getValue(b) - getValue(a);
    });
  }, [commissionStats.byPlace, summarySortState]);

  const sortedOfferSummary = useMemo(() => {
    const items = Array.from(commissionStats.byOffer.values());
    const sectionSort = summarySortState.offer || { key: 'total', direction: 'desc' };
    const getValue = (item) => {
      switch (sectionSort.key) {
        case 'count': return item.count;
        case 'total': return item.total;
        case 'neozoola': return item.neozoola;
        case 'ambassador': return item.ambassador;
        default: return item.total;
      }
    };
    return [...items].sort((a, b) => (sectionSort.direction === 'asc' ? getValue(a) - getValue(b) : getValue(b) - getValue(a)));
  }, [commissionStats.byOffer, summarySortState]);

  const sortedAmbassadorSummary = useMemo(() => {
    const items = Array.from(commissionStats.byAmbassador.values());
    const sectionSort = summarySortState.ambassador || { key: 'total', direction: 'desc' };
    const getValue = (item) => {
      switch (sectionSort.key) {
        case 'count': return item.count;
        case 'total': return item.total;
        case 'neozoola': return item.neozoola;
        case 'ambassador': return item.ambassador;
        default: return item.total;
      }
    };
    return [...items].sort((a, b) => (sectionSort.direction === 'asc' ? getValue(a) - getValue(b) : getValue(b) - getValue(a)));
  }, [commissionStats.byAmbassador, summarySortState]);

  return (
    <Box>
      <Typography variant="h4" sx={adminTabPageTitleSx}>
        Redemptions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={adminTabPageSubtitleSx}>
        View and filter redemption records, load per-place stats, and delete with audit. New redemptions are created only in the venue portal (scan or manual code) — not here.
      </Typography>
      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Where results show</Typography>
        <Typography variant="body2" component="div">
          After you click <strong>Apply filters</strong>, matching rows appear in <strong>Redemption records</strong> (table below).
          <strong> Search</strong> matches offer name, display code, ambassador code, or place/user <em>names</em> — not partial Place IDs.
          <strong> Place ID</strong> and <strong>User ID</strong> must be the full 24-character MongoDB IDs (copy from Places / Users tabs).
        </Typography>
      </Alert>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={adminTabCardSx}>
            <CardContent sx={adminTabCardContentSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <FilterList color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Filter and search
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}><TextField fullWidth label="Place ID (24 hex)" placeholder="e.g. 507f1f77bcf86cd799439011" value={localFilters.placeId || ''} onChange={(e) => setLocalFilters((p) => ({ ...p, placeId: e.target.value }))} helperText="Exact ID only — or leave blank and use Search" /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="User ID (24 hex)" value={localFilters.userId || ''} onChange={(e) => setLocalFilters((p) => ({ ...p, userId: e.target.value }))} helperText="Exact user _id" /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="Offer ID" value={localFilters.offerId || ''} onChange={(e) => setLocalFilters((p) => ({ ...p, offerId: e.target.value }))} /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="Ambassador code" value={localFilters.ambassadorCode || ''} onChange={(e) => setLocalFilters((p) => ({ ...p, ambassadorCode: e.target.value }))} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Search (names & text)" value={localFilters.search || ''} onChange={(e) => setLocalFilters((p) => ({ ...p, search: e.target.value }))} helperText="Place name, user name/email, offer, code…" /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth type="date" label="Redeemed from" InputLabelProps={{ shrink: true }} value={localFilters.dateFrom || ''} onChange={(e) => setLocalFilters((p) => ({ ...p, dateFrom: e.target.value }))} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth type="date" label="Redeemed to" InputLabelProps={{ shrink: true }} value={localFilters.dateTo || ''} onChange={(e) => setLocalFilters((p) => ({ ...p, dateTo: e.target.value }))} /></Grid>
                <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<Search />}
                    onClick={() => onApplyFilters(localFilters)}
                    sx={adminGradientContainedSx}
                  >
                    Apply filters
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={!isValidMongoObjectId(localFilters.placeId || '')}
                    onClick={() => onLoadPlaceStats(localFilters.placeId)}
                    sx={{ textTransform: 'none' }}
                    title={!isValidMongoObjectId(localFilters.placeId || '') ? 'Enter a valid 24-character Place ID first' : 'Load views, clicks, and per-offer redeems for this place'}
                  >
                    Load place stats
                  </Button>
                  <Chip label={`Matching total: ${total}`} color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Load place stats</strong> needs the same valid <strong>Place ID</strong> as above. It loads views/clicks and redemptions <em>per offer</em> for that venue (shown in the card below the table when loaded).
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ ...adminTabCardSx, background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.06) 0%, rgba(56, 142, 60, 0.04) 100%)' }}>
            <CardContent sx={adminTabCardContentSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TableChart color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Commission analytics
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {rows.length > 0 ? `Calculated from ${rows.length} redemption${rows.length !== 1 ? 's' : ''} matching your filters` : 'Apply filters to see commission data'}
              </Typography>

              {rows.length > 0 && (
                <>
                  {/* Top summary chips */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                    <Chip 
                      label={`Nezoola Commission: ${commissionStats.totalNeozoolaCommission.toFixed(2)} LE`} 
                      color="primary" 
                      sx={{ fontWeight: 600, fontSize: '0.95rem', px: 1 }}
                    />
                    <Chip
                      label={`Transaction Fees: ${commissionStats.totalTransactionFee.toFixed(2)} LE`}
                      color="secondary"
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: '0.95rem', px: 1 }}
                    />
                    <Chip
                      label={`Marketing Fees: ${commissionStats.totalMarketingFee.toFixed(2)} LE`}
                      color="secondary"
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: '0.95rem', px: 1 }}
                    />
                    <Chip
                      label={`Transaction Fees: ${commissionStats.totalTransactionFee.toFixed(2)} LE`}
                      color="secondary"
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: '0.95rem', px: 1 }}
                    />
                    <Chip 
                      label={`Total Offer: ${commissionStats.totalOfferCommission.toFixed(2)} LE`} 
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: '0.95rem', px: 1 }}
                    />
                    <Chip
                      label={`Transaction Fees: ${commissionStats.totalTransactionFee.toFixed(2)} LE`}
                      color="secondary"
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: '0.95rem', px: 1 }}
                    />
                  </Box>

                  {/* Commission by place */}
                  {commissionStats.byPlace.size > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>By Place</Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.08)' }}>
                              <TableCell sx={{ width: 40 }}></TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Place Name</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSummaryHeaderButton('place', 'count', 'Redeems')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSummaryHeaderButton('place', 'total', 'Total')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSummaryHeaderButton('place', 'marketingFee', 'Marketing Fee')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSummaryHeaderButton('place', 'neozoola', 'Nezoola Comm.')}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSummaryHeaderButton('place', 'ambassador', 'Ambassador Comm.')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedPlaceSummary.map((place) => (
                                <React.Fragment key={place.placeId}>
                                  <TableRow data-testid="summary-place-row">
                                    <TableCell sx={{ width: 40 }}>
                                      <IconButton 
                                        size="small" 
                                        onClick={() => setExpandedPlaces(prev => ({ ...prev, [place.placeId]: !prev[place.placeId] }))}
                                      >
                                        {expandedPlaces[place.placeId] ? <ExpandLess /> : <ExpandMore />}
                                      </IconButton>
                                    </TableCell>
                                    <TableCell>{place.placeName}</TableCell>
                                    <TableCell align="right">{place.count}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>{place.total.toFixed(2)} LE</TableCell>
                                     <TableCell align="right" sx={{ color: place.marketingFee > 0 ? 'secondary.main' : 'text.secondary' }}>{place.marketingFee.toFixed(2)} LE</TableCell>
                                    <TableCell align="right" sx={{ color: place.ambassador > 0 ? 'success.main' : 'text.secondary' }}>{place.ambassador.toFixed(2)} LE</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell colSpan={6} sx={{ p: 0 }}>
                                      <Collapse in={expandedPlaces[place.placeId]} timeout="auto" unmountOnExit>
                                        <Box sx={{ bgcolor: 'rgba(76, 175, 80, 0.04)', p: 2 }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Offers for {place.placeName}</Typography>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Offer</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Redeems</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Total</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Nezoola</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Ambassador</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {Array.from(commissionStats.placeOffers.get(place.placeId)?.values() || [])
                                                .sort((a, b) => b.count - a.count)
                                                .map((offer) => (
                                                  <TableRow key={offer.offerId} sx={{ backgroundColor: 'rgba(76, 175, 80, 0.02)' }}>
                                                    <TableCell sx={{ fontSize: '0.85rem' }}>{offer.offerName}</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.85rem' }}>{offer.count}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{offer.total.toFixed(2)} LE</TableCell>
                                                    <TableCell align="right" sx={{ color: offer.neozoola > 0 ? 'primary.main' : 'text.secondary', fontSize: '0.85rem' }}>{offer.neozoola.toFixed(2)} LE</TableCell>
                                                    <TableCell align="right" sx={{ color: offer.ambassador > 0 ? 'success.main' : 'text.secondary', fontSize: '0.85rem' }}>{offer.ambassador.toFixed(2)} LE</TableCell>
                                                  </TableRow>
                                                ))}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                              ))}
                            {/* Total row */}
                            <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', fontWeight: 700 }}>
                              <TableCell sx={{ fontWeight: 700 }}></TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {Array.from(commissionStats.byPlace.values()).reduce((sum, p) => sum + p.count, 0)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {Array.from(commissionStats.byPlace.values()).reduce((sum, p) => sum + p.total, 0).toFixed(2)} LE
                              </TableCell>
                               <TableCell align="right" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                                 {Array.from(commissionStats.byPlace.values()).reduce((sum, p) => sum + (p.marketingFee || 0), 0).toFixed(2)} LE
                               </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>
                                {Array.from(commissionStats.byPlace.values()).reduce((sum, p) => sum + p.ambassador, 0).toFixed(2)} LE
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {/* Commission by offer */}
                  {commissionStats.byOffer.size > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>By Offer</Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.08)' }}>
                              <TableCell sx={{ fontWeight: 600 }}>Offer</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSummaryHeaderButton('offer', 'count', 'Redeems')}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Place</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSummaryHeaderButton('offer', 'total', 'Total')}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSummaryHeaderButton('offer', 'neozoola', 'Nezoola Comm.')}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSummaryHeaderButton('offer', 'ambassador', 'Ambassador Comm.')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedOfferSummary.map((offer) => (
                                <TableRow key={offer.offerId}>
                                  <TableCell>{offer.offerName}</TableCell>
                                  <TableCell align="right">{offer.count}</TableCell>
                                  <TableCell>{Array.from(offer.places).join(', ')}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>{offer.total.toFixed(2)} LE</TableCell>
                                  <TableCell align="right" sx={{ color: offer.neozoola > 0 ? 'primary.main' : 'text.secondary' }}>{offer.neozoola.toFixed(2)} LE</TableCell>
                                  <TableCell align="right" sx={{ color: offer.ambassador > 0 ? 'success.main' : 'text.secondary' }}>{offer.ambassador.toFixed(2)} LE</TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {/* Commission by ambassador */}
                  {commissionStats.byAmbassador.size > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>By Ambassador</Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.08)' }}>
                              <TableCell sx={{ fontWeight: 600 }}>Ambassador Code</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSummaryHeaderButton('ambassador', 'count', 'Redeems')}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSummaryHeaderButton('ambassador', 'total', 'Total')}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSummaryHeaderButton('ambassador', 'neozoola', 'Nezoola Comm.')}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSummaryHeaderButton('ambassador', 'ambassador', 'Ambassador Comm.')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedAmbassadorSummary.map((amb) => (
                                <TableRow key={amb.code}>
                                  <TableCell>{amb.code}</TableCell>
                                  <TableCell align="right">{amb.count}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>{amb.total.toFixed(2)} LE</TableCell>
                                  <TableCell align="right" sx={{ color: amb.neozoola > 0 ? 'primary.main' : 'text.secondary' }}>{amb.neozoola.toFixed(2)} LE</TableCell>
                                  <TableCell align="right" sx={{ color: amb.ambassador > 0 ? 'success.main' : 'text.secondary' }}>{amb.ambassador.toFixed(2)} LE</TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card sx={adminTabCardSx}>
            <CardContent sx={adminTabCardContentSx}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Redemption records
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Rows returned by your current filters (click Apply filters after changing them). Read-only except delete (with reason).
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                       <TableCell>User</TableCell><TableCell>Place</TableCell><TableCell>Offer</TableCell><TableCell>Commission</TableCell>                     <TableCell>Marketing Fee</TableCell><TableCell>Ambassador</TableCell><TableCell>Date/Time</TableCell><TableCell>Code</TableCell><TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r._id}>
                        <TableCell>{r.userId?.name || r.userId?._id || r.userId}</TableCell>
                        <TableCell>{r.placeId?.name || r.placeId?._id || r.placeId}</TableCell>
                        <TableCell>{r.offerName || r.offerId}</TableCell>
                         <TableCell>
                           <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                             <Typography variant="body2">Base: {r.offerCommissionLe != null ? `${r.offerCommissionLe} LE` : '-'}</Typography>
                             <Typography variant="body2">Ambassador: {r.ambassadorCommissionPercentage != null ? `${r.ambassadorCommissionPercentage}%` : '-'}{r.ambassadorCommissionLe != null ? ` = ${r.ambassadorCommissionLe} LE` : ''}</Typography>
                             <Typography variant="body2" color="text.secondary">Net: {r.platformCommissionLe != null ? `${r.platformCommissionLe} LE` : '-'}</Typography>
                           </Box>
                         </TableCell>
                         <TableCell>{r.marketingFeeLe != null ? `${r.marketingFeeLe} LE` : (r.transactionFeeLe != null ? `${r.transactionFeeLe} LE` : '-')}</TableCell>
                        <TableCell>{r.ambassadorCode || '-'}</TableCell>
                        <TableCell>{r.redeemedAt ? new Date(r.redeemedAt).toLocaleString() : '-'}</TableCell>
                        <TableCell>{r.displayCode || '-'}</TableCell>
                        <TableCell align="right">
                          <Button color="error" size="small" startIcon={<Delete />} onClick={() => setDeleteDialog({ open: true, id: r._id })}>Delete</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                     {rows.length === 0 && <TableRow><TableCell colSpan={9} align="center">No redemptions match these filters — try Search, clear IDs, or widen dates.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {placeStats && (
          <Grid item xs={12}>
            <Card
              sx={{
                ...adminTabCardSx,
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.06) 0%, rgba(118, 75, 162, 0.04) 100%)',
              }}
            >
              <CardContent sx={adminTabCardContentSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TableChart color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Place stats
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {placeStats.placeName} ({placeStats.category || '—'}) — total redemptions: {placeStats.totalRedeems ?? 0}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip label={`Views: ${placeStats.totalViews ?? 0}`} size="small" color="primary" variant="outlined" />
                  <Chip label={`Clicks: ${placeStats.totalClicks ?? 0}`} size="small" color="primary" variant="outlined" />
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Offer</TableCell><TableCell align="right">Redeems</TableCell></TableRow></TableHead>
                    <TableBody>
                      {(placeStats.byOffer || []).map((o) => (
                        <TableRow key={o.offerId}><TableCell>{o.offerName || o.offerId}</TableCell><TableCell align="right">{o.count}</TableCell></TableRow>
                      ))}
                      {(placeStats.byOffer || []).length === 0 && <TableRow><TableCell colSpan={2} align="center">No offer redemptions yet</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <Card sx={adminTabCardSx}>
            <CardContent sx={adminTabCardContentSx}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Deletion history (audit)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead><TableRow><TableCell>Deleted By</TableCell><TableCell>At</TableCell><TableCell>Reason</TableCell><TableCell>Original</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(deletionHistory || []).map((h) => (
                      <TableRow key={h._id}>
                        <TableCell>{h.deletedBy?.name || h.deletedBy?._id || '-'}</TableCell>
                        <TableCell>{h.deletedAt ? new Date(h.deletedAt).toLocaleString() : '-'}</TableCell>
                        <TableCell>{h.reason}</TableCell>
                        <TableCell>{h.originalRedemption?.offerName || h.originalRedemption?.offerId || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {(deletionHistory || []).length === 0 && <TableRow><TableCell colSpan={4} align="center">No deletions yet</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={deleteDialog.open} onClose={() => { setDeleteDialog({ open: false, id: null }); setDeleteReason(''); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={adminDialogTitleSx}>Delete redemption</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason (required)"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDeleteDialog({ open: false, id: null }); setDeleteReason(''); }} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!deleteReason.trim()}
            sx={{ textTransform: 'none' }}
            onClick={async () => {
              await onDeleteRedemption(deleteDialog.id, deleteReason.trim());
              setDeleteDialog({ open: false, id: null });
              setDeleteReason('');
            }}
          >
            Delete and audit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
